import { useCallback, useEffect, useState, useRef } from 'react'
import {
  Room,
  RoomEvent,
  ConnectionState,
  Track,
  type RemoteTrack,
  type RemoteTrackPublication,
  type RemoteParticipant,
} from 'livekit-client'

import { CallUI } from '@/features/callUI/CallUI'
import type { CallState } from '@/features/callUI/types'
import { useDemoToken, useDemoRoomDelete } from './useDemoApi'

const TRANSCRIPT_TOPIC = 'livekit.transcript'
const AGENT_CONNECT_TIMEOUT = 20000

interface DemoVoiceCallProps {
  agentAltKey: string
}

export function DemoVoiceCall({ agentAltKey }: DemoVoiceCallProps) {
  const tokenMutation = useDemoToken()
  const roomDeleteMutation = useDemoRoomDelete()

  const [callState, setCallState] = useState<CallState>('idle')
  const [room, setRoom] = useState<Room | null>(null)
  const [isMuted, setIsMuted] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const [audioLevel, setAudioLevel] = useState(0)
  const [agentConnected, setAgentConnected] = useState(false)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [transcriptEvents, setTranscriptEvents] = useState<unknown[]>([])

  const audioLevelIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const agentTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const roomRef = useRef<Room | null>(null)
  const roomNameRef = useRef<string | null>(null)

  useEffect(() => {
    roomRef.current = room
  }, [room])

  const connectToRoom = useCallback(async (token: string, serverUrl: string) => {
    const newRoom = new Room({ adaptiveStream: true, dynacast: true })

    try {
      newRoom.on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
        if (state === ConnectionState.Connected) {
          setCallState('connected')
          setConnectionError(null)
        } else if (state === ConnectionState.Disconnected) {
          setCallState('idle')
        }
      })

      newRoom.on(
        RoomEvent.TrackSubscribed,
        (track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
          if (track.kind === Track.Kind.Audio) {
            const el = track.attach()
            el.id = `demo-audio-${participant.identity}`
            document.body.appendChild(el)
          }
        }
      )

      newRoom.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        track.detach().forEach((el) => el.remove())
      })

      newRoom.on(RoomEvent.Disconnected, () => {
        if (audioLevelIntervalRef.current) {
          clearInterval(audioLevelIntervalRef.current)
          audioLevelIntervalRef.current = null
        }
        if (agentTimeoutRef.current) {
          clearTimeout(agentTimeoutRef.current)
          agentTimeoutRef.current = null
        }
        setRoom(null)
        setIsMuted(false)
        setAudioLevel(0)
        setAgentConnected(false)
        roomNameRef.current = null
        document.querySelectorAll("audio[id^='demo-audio-']").forEach((el) => el.remove())
        setCallState('ended')
        setStatusMessage('Call ended')
      })

      newRoom.on(RoomEvent.ParticipantConnected, (participant: RemoteParticipant) => {
        if (participant.identity.includes('agent') || participant.isAgent) {
          setAgentConnected(true)
          setStatusMessage('Agent connected — start talking')
          if (agentTimeoutRef.current) {
            clearTimeout(agentTimeoutRef.current)
            agentTimeoutRef.current = null
          }
        }
      })

      newRoom.on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
        if (participant.identity.includes('agent') || participant.isAgent) {
          setAgentConnected(false)
          setStatusMessage('Agent disconnected')
        }
      })

      newRoom.on(
        RoomEvent.DataReceived,
        (payload: Uint8Array, _participant?: RemoteParticipant, _kind?: unknown, topic?: string) => {
          if (topic !== TRANSCRIPT_TOPIC) return
          try {
            const text = new TextDecoder().decode(payload)
            const message = JSON.parse(text) as { type: string; event?: unknown }
            if (message.type === 'event' && message.event) {
              setTranscriptEvents((prev) => [...prev, message.event])
            }
          } catch {
            // ignore malformed messages
          }
        }
      )

      await newRoom.connect(serverUrl, token)
      setRoom(newRoom)
      await newRoom.localParticipant.setMicrophoneEnabled(true)
      setStatusMessage('Connected, waiting for agent...')

      const existingAgents = Array.from(newRoom.remoteParticipants.values()).filter(
        (p) => p.identity.includes('agent') || p.isAgent
      )
      if (existingAgents.length > 0) {
        setAgentConnected(true)
        setStatusMessage('Agent connected — start talking')
      } else {
        agentTimeoutRef.current = setTimeout(() => {
          setConnectionError('Agent did not connect in time. Please try again.')
        }, AGENT_CONNECT_TIMEOUT)
      }

      audioLevelIntervalRef.current = setInterval(() => {
        const localTrack = newRoom.localParticipant.getTrackPublication(Track.Source.Microphone)
        if (localTrack?.track && !localTrack.isMuted) {
          setAudioLevel(Math.random() * 0.5 + 0.3)
        } else {
          setAudioLevel(0)
        }
      }, 100)
    } catch (error) {
      console.error('Demo room connection error:', error)
      setConnectionError(error instanceof Error ? error.message : 'Failed to connect')
      setCallState('idle')
      setRoom(null)
      roomNameRef.current = null
      try { await newRoom.disconnect() } catch { /* best-effort */ }
    }
  }, [])

  const startCall = useCallback(async () => {
    if (callState !== 'idle' && callState !== 'ended') return

    setCallState('connecting')
    setConnectionError(null)
    setTranscriptEvents([])
    setStatusMessage('Connecting...')

    try {
      const data = await tokenMutation.mutateAsync(agentAltKey)
      if (data.token && data.serverUrl && data.roomName) {
        roomNameRef.current = data.roomName
        await connectToRoom(data.token, data.serverUrl)
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to start call'
      setConnectionError(message)
      setCallState('idle')
      roomNameRef.current = null
    }
  }, [agentAltKey, callState, tokenMutation, connectToRoom])

  const endCall = useCallback(async () => {
    if (!room) return
    setCallState('disconnecting')

    if (audioLevelIntervalRef.current) {
      clearInterval(audioLevelIntervalRef.current)
      audioLevelIntervalRef.current = null
    }
    if (agentTimeoutRef.current) {
      clearTimeout(agentTimeoutRef.current)
      agentTimeoutRef.current = null
    }

    const rn = roomNameRef.current
    if (rn) {
      try { await roomDeleteMutation.mutateAsync(rn) } catch { /* best-effort */ }
    }

    try { await room.disconnect() } catch { /* best-effort */ }
    setRoom(null)
    setIsMuted(false)
    setAudioLevel(0)
    setAgentConnected(false)
    roomNameRef.current = null
    document.querySelectorAll("audio[id^='demo-audio-']").forEach((el) => el.remove())
    setCallState('ended')
    setStatusMessage('Call ended')
  }, [room, roomDeleteMutation])

  const toggleMute = useCallback(async () => {
    if (!room) return
    const next = !isMuted
    await room.localParticipant.setMicrophoneEnabled(!next)
    setIsMuted(next)
  }, [room, isMuted])

  const closePanel = useCallback(() => {
    setCallState('idle')
    setStatusMessage(null)
    setTranscriptEvents([])
  }, [])

  useEffect(() => {
    return () => {
      if (audioLevelIntervalRef.current) clearInterval(audioLevelIntervalRef.current)
      if (agentTimeoutRef.current) clearTimeout(agentTimeoutRef.current)
      const r = roomRef.current
      if (r) {
        const rn = roomNameRef.current
        if (rn) {
          const apiBase = import.meta.env.VITE_API_URL || '/api/v1'
          fetch(`${apiBase}/demo/room`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ roomName: rn }),
          }).catch(() => {})
        }
        r.disconnect()
        document.querySelectorAll("audio[id^='demo-audio-']").forEach((el) => el.remove())
      }
    }
  }, [])

  return (
    <CallUI
      variant="full"
      callState={callState}
      isMuted={isMuted}
      connectionError={connectionError}
      statusMessage={statusMessage}
      audioLevel={audioLevel}
      agentConnected={agentConnected}
      transcriptEvents={transcriptEvents}
      startCall={startCall}
      endCall={endCall}
      toggleMute={toggleMute}
      closePanel={closePanel}
    />
  )
}
